import type { PropertyManagerContext } from './types';

/**
 * 判断当前属性面板是否处于特征命令的独占会话中。
 *
 * 设计说明：
 * 1. 拉伸、旋转等特征进入 PropertyManager 后，应独占右侧属性面板；
 * 2. 草图选择面板、几何关系面板不应继续与特征面板并存，否则会把上一轮草图选择态残留带进特征配置；
 * 3. 该判断集中收口后，后续新增 sweep、loft 等特征时只需复用同一守卫即可。
 *
 * @param context PropertyManager 上下文。
 * @returns 是否处于特征独占会话。
 */
export function isFeaturePropertyManagerSessionActive(context: PropertyManagerContext): boolean {
  const extrudeState = context.extrudeState as { active?: boolean } | null | undefined;
  const revolveState = context.revolveState as { active?: boolean } | null | undefined;
  const revolveCutState = context.revolveCutState as { active?: boolean } | null | undefined;
  const extrudeActive = extrudeState?.active === true || context.extrudeActive === true;
  const revolveActive = revolveState?.active === true || context.revolveActive === true;
  const revolveCutActive = revolveCutState?.active === true || context.revolveCutActive === true;
  return extrudeActive || revolveActive || revolveCutActive;
}

//------ 草图圆角选择属性面板门禁 开始------
/**
 * 草图属性面板在独占草图命令中的显示策略。
 */
export enum SketchPropertySectionCommandSessionPolicy {
  /** 独占草图命令激活时拦截普通草图属性面板。 */
  BlockDuringExclusiveCommand = 'block-during-exclusive-command',
  /** 当前 section 自身就是独占命令面板，需要允许显示。 */
  AllowCurrentCommand = 'allow-current-command',
}

/**
 * 判断当前是否处于草图圆角/倒角 PropertyManager 独占命令。
 *
 * @param context PropertyManager 上下文。
 * @returns 草图圆角/倒角命令激活时返回 true。
 */
export function isSketchCornerPropertyManagerSessionActive(context: PropertyManagerContext): boolean {
  return context.sketchCornerEnable === true;
}
//------ 草图圆角选择属性面板门禁 结束------

//------ 智能尺寸命令面板门禁 开始------
/** 尺寸命令家族标识（与草图 workflow 的变体表一致）。 */
const SMART_DIMENSION_COMMAND_IDS = new Set([
  'sketch-smart-dimension',
  'sketch-horizontal-dimension',
  'sketch-vertical-dimension',
  'sketch-path-length-dimension',
  'sketch-chain-dimension',
  'sketch-baseline-dimension',
  'sketch-ordinate-dimension',
  'sketch-horizontal-ordinate-dimension',
  'sketch-vertical-ordinate-dimension',
]);

/**
 * 判断智能尺寸命令族会话是否激活。
 *
 * 说明：命令中点选实体是尺寸引用收集动作，实体属性 section 不得随选中叠加显示
 * （对齐 SOLIDWORKS：智能尺寸命令期间右侧不弹实体属性面板）。
 *
 * @param context PropertyManager 上下文。
 * @returns 是否处于智能尺寸命令族会话。
 */
export function isSmartDimensionPropertyManagerSessionActive(context: PropertyManagerContext): boolean {
  return typeof context.activeCommand === 'string' && SMART_DIMENSION_COMMAND_IDS.has(context.activeCommand);
}
//------ 智能尺寸命令面板门禁 结束------

/**
 * 判断草图属性面板在当前上下文中是否应该显示。
 *
 * @param context PropertyManager 上下文。
 * @param visible 草图面板自身的可见性条件。
 * @param commandSessionPolicy 独占草图命令中的显示策略。
 * @returns 是否允许显示草图属性面板。
 */
export function canShowSketchPropertySection(
  context: PropertyManagerContext,
  visible: boolean,
  commandSessionPolicy: SketchPropertySectionCommandSessionPolicy = SketchPropertySectionCommandSessionPolicy.BlockDuringExclusiveCommand,
): boolean {
  if (!visible) {
    return false;
  }
  //------ 草图属性面板模式守卫 开始------
  /**
   * 草图线、草图点、圆弧、圆、椭圆、槽口、样条等草图对象属性面板
   * 仅允许在草图编辑会话或草图绘制会话内显示。
   * 否则即便残留了选择态，也不能在非草图上下文中误显示。
   */
  if (!context.isSketchActive && !context.isDrawingMode) {
    return false;
  }
  //------ 草图属性面板模式守卫 结束------
  //------ 草图圆角选择属性面板门禁 开始------
  /**
   * 草图圆角/倒角命令激活后，右侧 PropertyManager 必须由命令面板独占。
   * 普通圆、圆弧、直线等属性 section 即便存在残留选中态也不能显示，
   * 否则命令选中的直线或圆弧会把实体属性面板叠到圆角命令面板上。
   */
  if (
    commandSessionPolicy === SketchPropertySectionCommandSessionPolicy.BlockDuringExclusiveCommand
    && isSketchCornerPropertyManagerSessionActive(context)
  ) {
    return false;
  }
  //------ 草图圆角选择属性面板门禁 结束------
  //------ 智能尺寸命令面板门禁 开始------
  if (
    commandSessionPolicy === SketchPropertySectionCommandSessionPolicy.BlockDuringExclusiveCommand
    && isSmartDimensionPropertyManagerSessionActive(context)
  ) {
    return false;
  }
  //------ 智能尺寸命令面板门禁 结束------
  return !isFeaturePropertyManagerSessionActive(context);
}

/**
 * 处理草图实体属性面板的确认动作。
 *
 * 设计说明：
 * 1. 绘图态下，确认按钮仍沿用宿主已有的绘图/命令确认语义；
 * 2. 通过“单击选择实体”打开属性面板时，确认按钮语义统一为“清空当前选择并关闭面板”；
 * 3. 这样可以保证线、圆、圆弧、椭圆、点、槽口、样条等草图实体面板交互一致。
 *
 * @param context PropertyManager 上下文。
 */
export function handleSketchEntityPropertyConfirm(context: PropertyManagerContext): void {
  if (!context.isDrawingMode) {
    context.clearSelection?.();
    return;
  }
  context.onConfirm?.();
}

/**
 * 判断几何关系面板在当前上下文中是否应该显示。
 *
 * @param context PropertyManager 上下文。
 * @returns 是否允许显示几何关系面板。
 */
export function canShowGeometryRelationsSection(context: PropertyManagerContext): boolean {
  //------ 几何关系面板显示守卫 开始------
  /**
   * 几何关系面板仅允许在草图编辑会话显示：
   * 1. 避免默认模式/特征模式的多选误进入草图关系面板；
   * 2. 多选来源统一为“当前选中对象数量 > 1”的候选态；
   * 3. 特征命令会话始终拥有更高优先级。
   */
  if (isFeaturePropertyManagerSessionActive(context) || isSketchCornerPropertyManagerSessionActive(context)) {
    return false;
  }
  if (context.isSketchActive !== true) {
    return false;
  }
  return context.multipleEntitiesBoxSelected === true;
  //------ 几何关系面板显示守卫 结束------
}
